import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, PenLine, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtKr = (v) => (v == null ? '–' : `${Math.round(v).toLocaleString('is-IS')} kr.`);
const fmtPct = (v) => (v == null ? '–' : `${v.toLocaleString('is-IS')}%`);

const isMonthly = (r) => r.pay_method === 'monthly' || r.salary_basis === 'monthly_fixed';

const payFormLabel = (r) => {
  const pm = r.pay_method || (r.salary_basis === 'monthly_fixed' ? 'monthly' : r.salary_basis ? 'hourly' : '');
  if (!pm) return 'Eldra fyrirkomulag';
  const rs = r.rate_source || (r.salary_basis === 'wage_table' ? 'wage_table' : 'manual');
  return `${pm === 'monthly' ? 'Mánaðarlaun' : 'Tímakaup'} · ${rs === 'wage_table' ? 'Launatafla' : 'Sérsamin laun'}`;
};

export const PayrollPreview = ({ open, onOpenChange, fromDate, toDate }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overrideFor, setOverrideFor] = useState(null); // employee_id
  const [ovPct, setOvPct] = useState('');
  const [ovReason, setOvReason] = useState('');

  const fetchRows = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/payroll/preview`, {
        params: { from_date: fromDate, to_date: toDate },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(res.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Villa við að sækja launakeyrslu');
    }
    setLoading(false);
  }, [fromDate, toDate]);

  useEffect(() => {
    if (open) fetchRows();
  }, [open, fetchRows]);

  const setOverride = async (employeeId) => {
    if (!ovReason.trim()) {
      toast.error('Ástæða er skyldureitur');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/payroll/override`, {
        employee_id: employeeId, period_from: fromDate, period_to: toDate,
        percentage: parseFloat(ovPct), reason: ovReason.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Handvirkt greiðsluhlutfall vistað (audit-skráð)');
      setOverrideFor(null); setOvPct(''); setOvReason('');
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Villa við að vista override');
    }
  };

  const removeOverride = async (overrideId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/payroll/override/${overrideId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Override fjarlægt – helst í audit-sögu');
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Villa');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="payroll-preview-dialog">
        <DialogHeader>
          <DialogTitle>Launakeyrsla {fromDate} – {toDate}</DialogTitle>
        </DialogHeader>
        {loading && <p className="text-sm text-gray-500">Reikna...</p>}
        <div className="space-y-4">
          {rows.map((r) => (
            <div key={r.employee_id} className="border rounded-lg p-4" data-testid={`payroll-row-${r.employee_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-semibold">{r.name}</span>
                  <span className="ml-2 text-xs text-gray-500">{r.ssn}</span>
                  <Badge variant="outline" className="ml-2">{payFormLabel(r)}</Badge>
                </div>
                {r.needs_review && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`needs-review-${r.employee_id}`}>
                    <AlertTriangle className="h-3 w-3 mr-1" /> Þarfnast yfirferðar
                  </Badge>
                )}
              </div>
              {(r.warnings || []).map((w, i) => (
                <p key={i} className="text-xs text-amber-700 mb-1">{w}</p>
              ))}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-sm">
                {isMonthly(r) ? (
                  <>
                    <div><span className="text-gray-500">Umsamin mánaðarlaun:</span> <b data-testid={`contract-salary-${r.employee_id}`}>{fmtKr(r.contract_monthly_salary)}</b></div>
                    <div><span className="text-gray-500">Starfshlutfall:</span> {fmtPct(r.employment_percentage)}</div>
                    <div><span className="text-gray-500">Áætlaðar stundir:</span> {r.scheduled_hours} klst</div>
                    <div><span className="text-gray-500">Unninn tími:</span> {r.worked_hours} klst</div>
                    <div><span className="text-gray-500">Greidd veikindi:</span> {r.paid_sick_hours} klst</div>
                    <div><span className="text-gray-500">Greitt orlof:</span> {r.paid_vacation_hours} klst</div>
                    <div><span className="text-gray-500">Önnur greidd fjarvist:</span> {(r.paid_holiday_hours || 0) + (r.contract_holiday_hours || 0)} klst</div>
                    <div><span className="text-gray-500">Ógreidd fjarvist:</span> <span className={r.unpaid_absence_hours > 0 ? 'text-red-600 font-medium' : ''}>{r.unpaid_absence_hours} klst</span></div>
                    <div><span className="text-gray-500">Mæting:</span> <span data-testid={`attendance-${r.employee_id}`}>{fmtPct(r.attendance_percentage)}</span></div>
                    <div>
                      <span className="text-gray-500">Greiðsluhlutfall:</span>{' '}
                      <b data-testid={`payable-pct-${r.employee_id}`}>{fmtPct(r.salary_payable_percentage)}</b>
                      {r.manual_override && (
                        <Badge className="ml-1 bg-purple-100 text-purple-800 border-purple-300" data-testid={`manual-override-badge-${r.employee_id}`}>
                          Handvirkt
                        </Badge>
                      )}
                    </div>
                    <div><span className="text-gray-500">Mánaðarlaun til greiðslu:</span> <b className="text-green-700" data-testid={`salary-payable-${r.employee_id}`}>{fmtKr(r.monthly_salary_payable)}</b></div>
                    <div><span className="text-gray-500">Yfirvinna:</span> {r.overtime_hours} klst {r.overtime_payable ? `(${fmtKr(r.overtime_payable)})` : ''}</div>
                    <div><span className="text-gray-500">Stórhátíð:</span> {r.major_holiday_hours} klst {r.major_holiday_payable ? `(${fmtKr(r.major_holiday_payable)})` : ''}</div>
                    {r.advance_deduction > 0 && <div><span className="text-gray-500">Frádráttur (fyrirfram):</span> {fmtKr(r.advance_deduction)}</div>}
                    {r.car_allowance > 0 && <div><span className="text-gray-500">Ökutækjastyrkur:</span> {fmtKr(r.car_allowance)}</div>}
                  </>
                ) : (
                  <>
                    <div><span className="text-gray-500">Dagvinna:</span> {r.day_hours} klst {r.hourly_day_pay ? `(${fmtKr(r.hourly_day_pay)})` : ''}</div>
                    <div><span className="text-gray-500">Yfirvinna:</span> {r.overtime_hours} klst {r.overtime_payable ? `(${fmtKr(r.overtime_payable)})` : ''}</div>
                    <div><span className="text-gray-500">Stórhátíð:</span> {r.major_holiday_hours} klst</div>
                    <div><span className="text-gray-500">Greidd veikindi:</span> {r.paid_sick_hours} klst</div>
                    <div><span className="text-gray-500">Taxti dagvinna:</span> {r.day_rate ? `${r.day_rate.toLocaleString('is-IS')} kr/klst` : '–'}</div>
                    <div><span className="text-gray-500">Taxti yfirvinna:</span> {r.overtime_rate ? `${r.overtime_rate.toLocaleString('is-IS')} kr/klst` : '–'}</div>
                    <div><span className="text-gray-500">Mæting:</span> {fmtPct(r.attendance_percentage)}</div>
                    <div><span className="text-gray-500">Ógreidd fjarvist:</span> {r.unpaid_absence_hours} klst</div>
                  </>
                )}
              </div>
              {r.manual_override && r.override && (
                <div className="mt-2 text-xs bg-purple-50 border border-purple-200 rounded p-2" data-testid={`override-info-${r.employee_id}`}>
                  Handvirkt greiðsluhlutfall {fmtPct(r.override.percentage)} sett af {r.override.set_by}
                  {r.override.auto_percentage_at_set != null && <> (sjálfreiknað var {fmtPct(r.override.auto_percentage_at_set)})</>}
                  {' '}– Ástæða: {r.override.reason}
                  <Button variant="ghost" size="sm" className="ml-2 h-6 text-red-600"
                    onClick={() => removeOverride(r.override.id)}
                    data-testid={`remove-override-${r.employee_id}`}>
                    <X className="h-3 w-3 mr-1" /> Fjarlægja
                  </Button>
                </div>
              )}
              {isMonthly(r) && !r.manual_override && (
                overrideFor === r.employee_id ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                    <Input type="number" step="0.01" placeholder="%" value={ovPct}
                      onChange={(e) => setOvPct(e.target.value)}
                      className="w-24 h-8" data-testid={`override-pct-input-${r.employee_id}`} />
                    <Input placeholder="Ástæða (skylda)" value={ovReason}
                      onChange={(e) => setOvReason(e.target.value)}
                      className="w-64 h-8" data-testid={`override-reason-input-${r.employee_id}`} />
                    <Button size="sm" className="h-8" onClick={() => setOverride(r.employee_id)}
                      data-testid={`override-save-${r.employee_id}`}>Vista</Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => setOverrideFor(null)}>Hætta við</Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="mt-2 h-7 text-xs"
                    onClick={() => { setOverrideFor(r.employee_id); setOvPct(String(r.auto_payable_percentage ?? 100)); setOvReason(''); }}
                    data-testid={`override-btn-${r.employee_id}`}>
                    <PenLine className="h-3 w-3 mr-1" /> Handvirkt greiðsluhlutfall
                  </Button>
                )
              )}
            </div>
          ))}
          {!loading && rows.length === 0 && <p className="text-sm text-gray-500">Engir starfsmenn á tímabilinu.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PayrollPreview;
